import { Form, Link } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { MasterclassModuleCoverImageField } from "@/components/masterclasses/masterclass-module-cover-image-field"
import { MasterclassModuleTypeBadge } from "@/components/masterclasses/masterclass-module-type-badge"
import {
  MasterclassModuleUploadField,
  type UploadStatus,
} from "@/components/masterclasses/masterclass-module-upload-field"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import {
  masterclassModulePath,
  masterclassModulesPath,
  masterclassPath,
} from "@/routes"
import type { MasterclassModuleRow } from "@/types/masterclasses"

const TITLE_MAX = 120
const DESCRIPTION_MAX = 1_000

type FormMode = "link" | "upload"

/**
 * OKS-55 — Shared `<MasterclassModuleForm>` body for `New` and `Edit`
 * pages (TRD KD-8). Mirrors the OKS-54 `<MasterclassForm>` structure:
 * 2/3 form column on desktop ≥1024px + 1/3 right-rail Summary card;
 * single-column stack below.
 *
 * **Crew hard rule (P0):** uses Inertia `<Form>` from `@inertiajs/react`
 * with plain shadcn primitives + `name` attributes. **NO** shadcn
 * `Form` / `FormField` / `FormItem` / `FormMessage`; **NO**
 * `react-hook-form`; **NO** `zod`. Errors render manually from the
 * render-prop `errors` object.
 *
 * Mode discriminator (`link` / `upload`):
 *   - RadioGroup local state; default "link" on `new`, pre-selected to
 *     the persisted mode on `edit` (or "link" when both fields are
 *     empty — degenerate state, shouldn't happen post-OKS-55 XOR).
 *   - Conditional sub-field renders the URL `Input` OR the upload
 *     `<MasterclassModuleUploadField>` based on local mode.
 *   - On swap: the muted note "Switching modes clears the previous
 *     selection" surfaces per UX Spec §4. Submit button disabled while
 *     the direct upload is in flight (`uploadStatus === "uploading"`).
 *
 * Mode-swap server contract (TRD KD-7):
 *   - mode=link → controller schedules `video.detach` + post-commit
 *     `purge_later`. Form only needs to submit `link_url` + `mode=link`.
 *   - mode=upload → controller clears `link_url: nil` in the same
 *     `update!`. Form submits `mode=upload` + the new `:video` signed_id.
 */
export interface MasterclassModuleFormProps {
  masterclass: { id: number; title: string | null }
  module: MasterclassModuleRow
  mode: "new" | "edit"
  directUploadUrl: string
  /** Soft cap (typically 50). Drives the right-rail summary copy. */
  capHard: number
}

export function MasterclassModuleForm({
  masterclass,
  module: mod,
  mode,
  directUploadUrl,
  capHard,
}: MasterclassModuleFormProps) {
  const { t } = useTranslation()

  // Form state — controlled inputs so the right-rail summary previews
  // live + char counters tick.
  const [title, setTitle] = useState(mod.title ?? "")
  const [description, setDescription] = useState(mod.description ?? "")
  const [linkUrl, setLinkUrl] = useState(mod.link_url ?? "")
  // Default mode resolution:
  //   - persisted "link" / "upload" wins on edit
  //   - otherwise "link" (matches UX Spec §2.2 default)
  const [formMode, setFormMode] = useState<FormMode>(
    mod.mode === "upload" ? "upload" : "link",
  )
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    mod.has_video ? "ready" : "idle",
  )

  // Track whether the user has explicitly switched modes — drives the
  // "Switching modes clears the previous selection" warning copy.
  const initialMode = mod.mode === "upload" ? "upload" : "link"
  const modeSwapped = mode === "edit" && initialMode !== formMode

  const action =
    mode === "new"
      ? masterclassModulesPath(masterclass.id)
      : masterclassModulePath(masterclass.id, mod.id ?? 0)

  const httpMethod = mode === "new" ? "post" : "patch"

  const cancelHref = masterclassPath(masterclass.id)

  const submitDisabled = formMode === "upload" && uploadStatus === "uploading"

  return (
    <Form
      action={action}
      method={httpMethod}
      className="grid gap-6 lg:grid-cols-3"
      data-testid={`masterclass-module-form-${mode}`}
    >
      {({ processing, errors }) => (
        <>
          {/* ---------- Form column ---------- */}
          <div className="flex flex-col gap-6 lg:col-span-2">
            {/* Soft-cap / XOR base error banner */}
            {errors.base && (
              <div
                className="border-destructive bg-destructive/10 text-destructive rounded-md border p-3 text-sm"
                role="alert"
                data-testid="masterclass-module-form-base-error"
              >
                {errors.base}
              </div>
            )}

            {/* Title */}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="masterclass_module_title">
                  {t("masterclasses.modules.form.title.label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <span
                  className="text-muted-foreground text-xs tabular-nums"
                  aria-hidden="true"
                >
                  {t("masterclasses.modules.counter", {
                    count: title.length,
                    max: TITLE_MAX,
                  })}
                </span>
              </div>
              <Input
                id="masterclass_module_title"
                name="masterclass_module[title]"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={TITLE_MAX}
                placeholder={t("masterclasses.modules.form.title.placeholder")}
                aria-invalid={errors.title ? "true" : undefined}
                aria-describedby={
                  errors.title ? "masterclass_module_title_error" : undefined
                }
                data-testid="masterclass-module-form-title"
                aria-required="true"
              />
              {errors.title && (
                <p
                  id="masterclass_module_title_error"
                  className="text-destructive text-sm"
                  role="alert"
                  data-testid="masterclass-module-form-title-error"
                >
                  {errors.title}
                </p>
              )}
            </div>

            {/* OKS-833 — Module cover (3:2). Independent of the link/upload
                video radio below (a cover is orthogonal to the video
                source XOR). Hidden multipart input lives inside this
                parent Inertia <Form>. */}
            <MasterclassModuleCoverImageField
              currentUrl={mod.cover_image_url ?? null}
              hasCoverImage={mod.has_cover_image ?? false}
              moduleId={mod.id}
              error={errors.cover_image}
            />

            {/* Description */}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="masterclass_module_description">
                  {t("masterclasses.modules.form.description.label")}
                </Label>
                <span
                  className="text-muted-foreground text-xs tabular-nums"
                  aria-hidden="true"
                >
                  {t("masterclasses.modules.counter", {
                    count: description.length,
                    max: DESCRIPTION_MAX,
                  })}
                </span>
              </div>
              <Textarea
                id="masterclass_module_description"
                name="masterclass_module[description]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={DESCRIPTION_MAX}
                rows={8}
                placeholder={t(
                  "masterclasses.modules.form.description.placeholder",
                )}
                aria-invalid={errors.description ? "true" : undefined}
                aria-describedby={
                  errors.description
                    ? "masterclass_module_description_error"
                    : undefined
                }
                data-testid="masterclass-module-form-description"
              />
              {errors.description && (
                <p
                  id="masterclass_module_description_error"
                  className="text-destructive text-sm"
                  role="alert"
                  data-testid="masterclass-module-form-description-error"
                >
                  {errors.description}
                </p>
              )}
            </div>

            {/* Mode radio group */}
            <div className="flex flex-col gap-2">
              <Label>{t("masterclasses.modules.form.mode.label")}</Label>
              <RadioGroup
                value={formMode}
                onValueChange={(v: string) => setFormMode(v as FormMode)}
                name="masterclass_module[mode]"
                className="grid gap-3 sm:grid-cols-2"
                data-testid="masterclass-module-form-mode"
              >
                {/* Inline `t()` calls per mode so the i18n health
                    scanner sees each literal key at the call site. */}
                <ModeRadioCard
                  value="link"
                  title={t("masterclasses.modules.form.mode.link_card_title")}
                  body={t("masterclasses.modules.form.mode.link_card_body")}
                  selected={formMode === "link"}
                  testId="masterclass-module-form-mode-link"
                />
                <ModeRadioCard
                  value="upload"
                  title={t("masterclasses.modules.form.mode.upload_card_title")}
                  body={t("masterclasses.modules.form.mode.upload_card_body")}
                  selected={formMode === "upload"}
                  testId="masterclass-module-form-mode-upload"
                />
              </RadioGroup>
              {modeSwapped && (
                <p
                  className="text-muted-foreground text-xs"
                  data-testid="masterclass-module-form-mode-swap-warning"
                >
                  {t("masterclasses.modules.form.mode.swap_warning")}
                </p>
              )}
            </div>

            {/* Conditional sub-field — URL or Upload */}
            {formMode === "link" ? (
              <div className="flex flex-col gap-2">
                <Label htmlFor="masterclass_module_link_url">
                  {t("masterclasses.modules.form.link_url.label")}
                </Label>
                <Input
                  id="masterclass_module_link_url"
                  name="masterclass_module[link_url]"
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder={t(
                    "masterclasses.modules.form.link_url.placeholder",
                  )}
                  aria-invalid={errors.link_url ? "true" : undefined}
                  aria-describedby={
                    errors.link_url
                      ? "masterclass_module_link_url_error"
                      : "masterclass_module_link_url_helper"
                  }
                  data-testid="masterclass-module-form-link-url"
                />
                <p
                  id="masterclass_module_link_url_helper"
                  className="text-muted-foreground text-xs"
                >
                  {t("masterclasses.modules.form.link_url.helper")}
                </p>
                {errors.link_url && (
                  <p
                    id="masterclass_module_link_url_error"
                    className="text-destructive text-sm"
                    role="alert"
                    data-testid="masterclass-module-form-link-url-error"
                  >
                    {errors.link_url}
                  </p>
                )}
              </div>
            ) : (
              <MasterclassModuleUploadField
                fieldName="masterclass_module[video]"
                directUploadUrl={directUploadUrl}
                initialFilename={mod.has_video ? "current-video" : null}
                initialSignedId={null}
                onStatusChange={setUploadStatus}
                errorMessage={errors.video ?? null}
              />
            )}

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button asChild variant="outline" type="button">
                <Link
                  href={cancelHref}
                  data-testid="masterclass-module-form-cancel"
                >
                  {t("masterclasses.modules.form.actions.cancel")}
                </Link>
              </Button>
              <Button
                type="submit"
                disabled={processing || submitDisabled}
                data-testid="masterclass-module-form-submit"
              >
                {(processing || submitDisabled) && (
                  <Loader2
                    className="mr-2 size-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                {mode === "new"
                  ? t("masterclasses.modules.form.actions.save_new")
                  : t("masterclasses.modules.form.actions.save_edit")}
              </Button>
            </div>
          </div>

          {/* ---------- Summary right-rail ---------- */}
          <Card
            className="h-fit lg:col-span-1"
            data-testid="masterclass-module-form-summary"
          >
            <CardHeader>
              <CardTitle>
                {t("masterclasses.modules.summary_heading")}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-muted-foreground text-xs uppercase">
                  {t("masterclasses.modules.form.title.label")}
                </p>
                <p className="text-sm font-medium">
                  {title || (
                    <span className="text-muted-foreground italic">—</span>
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-muted-foreground text-xs uppercase">
                  {t("masterclasses.modules.form.description.label")}
                </p>
                <p className="text-muted-foreground line-clamp-4 text-sm">
                  {description || <span className="italic">—</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 border-t pt-3">
                <p className="text-muted-foreground text-xs uppercase">
                  {t("masterclasses.modules.summary_mode_label")}
                </p>
                <MasterclassModuleTypeBadge mode={formMode} />
              </div>
              <p
                className="text-muted-foreground text-xs"
                data-testid="masterclass-module-form-summary-position"
              >
                {mode === "new"
                  ? t("masterclasses.modules.summary_position_new", {
                      n: (mod.position ?? 0) + 1,
                    })
                  : t("masterclasses.modules.summary_position_edit", {
                      n: (mod.position ?? 0) + 1,
                    })}
                {" · "}
                {capHard}
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </Form>
  )
}

// ---------------------------------------------------------------------

function ModeRadioCard({
  value,
  title,
  body,
  selected,
  testId,
}: {
  value: FormMode
  /** Pre-translated copy — parent inlines `t()` per mode so the i18n
   *  health scanner sees each literal key at the call site. */
  title: string
  body: string
  selected: boolean
  testId: string
}) {
  // OKS-958 (deferred by OKS-954, now fixed) — accessible name/description.
  //
  // 🔴 READ THIS BEFORE APPLYING THE SIBLING PATTERN. OKS-954 narrowed two
  // sibling components (`mission-assign-mode-picker`, `mission-type-selector`)
  // to `aria-labelledby`→title and deliberately did NOT touch this card. The
  // reason matters: those two already had an `aria-describedby` carrying their
  // helper text, so narrowing the name RELOCATED the helper. This card had no
  // description wiring at all, so the body reached the user ONLY through the
  // accessible name -- the whole card is the <label>, so the native-label
  // computation swept title + body into it as "TitreCorps". Adding
  // `aria-labelledby` alone here would not have narrowed the name, it would
  // have DELETED the body.
  //
  // That is why the fix is both attributes together. If you are here to apply
  // the house pattern, apply both or neither.
  //
  // Ids are seeded from `testId`, which is already unique per card, so the two
  // cards cannot collide. Both must resolve: an unresolvable `aria-labelledby`
  // silently yields an EMPTY name rather than falling back to the label.
  const titleId = `${testId}-title`
  const bodyId = `${testId}-body`

  return (
    <Label
      htmlFor={testId}
      className={`flex cursor-pointer flex-col gap-2 rounded-md border p-3 text-sm transition-colors ${
        selected ? "border-primary bg-primary/5" : "border-input"
      }`}
      data-testid={`${testId}-card`}
      data-selected={selected ? "true" : "false"}
    >
      <div className="flex items-center gap-2">
        <RadioGroupItem
          id={testId}
          value={value}
          data-testid={testId}
          aria-labelledby={titleId}
          aria-describedby={bodyId}
        />
        <span id={titleId} className="font-medium">
          {title}
        </span>
      </div>
      <p id={bodyId} className="text-muted-foreground text-xs">
        {body}
      </p>
    </Label>
  )
}
